import { Link } from "react-router-dom";
import { ArrowRight, Network, Cable, Building2, Users } from "lucide-react";

const projectTypes = [
  {
    icon: Network,
    title: "Fiber Backbone Builds",
    description: "Large-scale fiber backbone construction projects connecting communities and data centers."
  },
  {
    icon: Cable,
    title: "Distribution Network Expansions",
    description: "Network expansion projects bringing fiber and broadband to new service areas."
  },
  {
    icon: Building2,
    title: "Broadband Infrastructure Upgrades",
    description: "Upgrading existing infrastructure to support next-generation broadband services."
  },
  {
    icon: Users,
    title: "Multi-Crew Large-Scale Deployments",
    description: "Coordinated multi-crew deployments for time-sensitive, large-scale projects."
  }
];

const experienceHighlights = [
  "Completed fiber backbone projects spanning multiple states",
  "Supported distribution network builds for major carriers",
  "Delivered emergency restoration services with rapid response",
  "Successfully executed multi-phase infrastructure upgrades",
  "Partnered with prime contractors on nationwide deployments"
];

export default function Projects() {
  return (
    <div data-testid="projects-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#0F172A]" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              Project Experience
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight text-white mt-4 mb-6">
              Proven Track Record
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              C&F Communications has supported telecommunications deployments across 
              multiple states in collaboration with major carriers and contractors.
            </p>
          </div>
        </div>
      </section>

      {/* Experience Highlights */}
      <section className="py-20 md:py-32 bg-white" data-testid="experience-highlights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                Our Experience
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
                Experience Highlights
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Our team brings extensive experience in telecommunications infrastructure 
                construction, having worked on projects of various scales across the nation.
              </p>
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8]">
                <ul className="space-y-4">
                  {experienceHighlights.map((item, index) => (
                    <li key={index} className="flex items-start gap-4" data-testid={`highlight-${index}`}>
                      <div className="w-2 h-2 bg-[#00B4D8] mt-2 flex-shrink-0"></div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-[#0F172A] p-8 text-white">
                <div className="text-5xl font-black text-[#00B4D8] mb-2">Multi-State</div>
                <div className="text-sm uppercase tracking-wider text-slate-400">Project Coverage</div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-50 p-6 text-center border-l-4 border-[#00B4D8]">
                  <div className="text-3xl font-black text-slate-900 mb-1">OH</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Ohio HQ</div>
                </div>
                <div className="bg-slate-50 p-6 text-center border-l-4 border-[#F59E0B]">
                  <div className="text-3xl font-black text-slate-900 mb-1">2022</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="project-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              What We Build
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Project Types
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projectTypes.map((project, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 p-8 hover:border-[#00B4D8] transition-colors group"
                data-testid={`project-type-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00B4D8] transition-colors">
                    <project.icon className="h-7 w-7 text-[#00B4D8] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                      {project.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {project.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#00B4D8]" data-testid="projects-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Have a Project in Mind?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Let's discuss how C&F Communications can support your next telecommunications project.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="projects-cta-button"
          >
            Start a Conversation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
